import {
  configureFormatDefaults,
  type DateFormatOptions,
} from '@ubus/shared';

export const ADMIN_TZ_STORAGE_KEY = 'ubus-admin-timezone';

/** `local` = follow the browser; otherwise an IANA zone id. */
export type AdminDisplayTimezone = 'local' | (string & {});

export const ADMIN_TIMEZONE_OPTIONS: Array<{ value: AdminDisplayTimezone; labelKey: string }> = [
  { value: 'local', labelKey: 'settings.tzBrowser' },
  { value: 'Australia/Sydney', labelKey: 'settings.tzSydney' },
  { value: 'Australia/Melbourne', labelKey: 'settings.tzMelbourne' },
  { value: 'Australia/Brisbane', labelKey: 'settings.tzBrisbane' },
  { value: 'Australia/Perth', labelKey: 'settings.tzPerth' },
  { value: 'UTC', labelKey: 'settings.tzUtc' },
  { value: 'Asia/Shanghai', labelKey: 'settings.tzShanghai' },
];

export function readStoredDisplayTimezone(): AdminDisplayTimezone {
  const raw = localStorage.getItem(ADMIN_TZ_STORAGE_KEY);
  if (!raw || raw === 'local') return 'local';
  if (ADMIN_TIMEZONE_OPTIONS.some((o) => o.value === raw)) return raw;
  // Allow custom IANA strings typed/stored later
  if (/^[A-Za-z0-9_+-]+\/[A-Za-z0-9_+-]+$/.test(raw) || raw === 'UTC') return raw;
  return 'local';
}

export function persistDisplayTimezone(tz: AdminDisplayTimezone): void {
  localStorage.setItem(ADMIN_TZ_STORAGE_KEY, tz);
  window.dispatchEvent(new CustomEvent('ubus-admin-timezone'));
}

/** Apply shared formatter defaults from Admin language + timezone preference. */
export function syncFormatDefaults(args: {
  language: string;
  timezone: AdminDisplayTimezone;
}): void {
  const locale = args.language === 'zh-CN' ? 'zh-CN' : 'en-AU';
  const next: Partial<DateFormatOptions> = { locale };
  if (args.timezone === 'local') {
    configureFormatDefaults({ locale, timeZone: undefined });
  } else {
    configureFormatDefaults({ locale, timeZone: args.timezone });
  }
  void next;
}

export function browserTimeZoneId(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
  } catch {
    return 'local';
  }
}
